import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { getSmsInbound } from '@lib/care-api';
import { currentClient, DEFAULT_CLIENT } from '@lib/clients';
import { isReceiveOnly } from '@lib/receive-only';

/**
 * The unread-text badge (William, 2026-07-10). ONE console-wide poller of /api/care/sms/inbound (a
 * PHI-minimized proxy of cdx-sms's reply feed) that badges members with a NEW inbound text since the
 * operator last looked. Faster than the queue's ingest cycle - a live "a member just texted" signal.
 *
 * NEW = arrived AFTER we started watching. On the very first run there is no stored cursor, so we
 * PRIME: fast-forward past all existing history without badging any of it, and only badge future
 * arrivals. Otherwise every operator would open the console to a badge of the entire back-catalog.
 *
 * The console persists ONLY the opaque cursor plus a bounded dedupe set and the current unread map -
 * never message bodies (the feed carries none). Opening a member's conversation clears its badge.
 *
 * A RECEIVE-ONLY PLAN HAS NO BADGE (QA-1070 word 1; the office's rulings of 4 Oct 2026 3:25 and 3:29
 * PM; Client Wall rule 1). The feed is Alignment's texting app, so for any plan without a texting
 * number of its own (lib/receive-only) this provider never polls, shows no count, and on mount DELETES
 * what an earlier build stored under that plan's key in this browser (Alignment's phone keys and message
 * ids, gathered while a BCBSM or Zing view polled). Alignment's own key is never touched. The server
 * refuses the read as well (api/src/care/sms-inbound.ts), so this half keeps the wire and the screen
 * quiet rather than being the wall.
 *
 * ROW 866 (William, 4 Oct 2026: "Show the real count"): `unreadCount` is no longer shown on the left
 * column's 01 Inbound queue line, which now carries the queue's open count (lib/inbound-open-count.ts).
 * Nothing displays the unread count today; this provider, its poll and markSeen are kept unchanged.
 */

const LS_KEY_BASE = 'membercare.sms.inbound.v1';
/** Per-client cursor + seen set. Alignment keeps the historical key so nothing re-badges on upgrade;
 * the provider is remounted by AppShell on a client switch, so reading this at mount is enough. */
function lsKey(): string {
  const c = currentClient();
  return c === DEFAULT_CLIENT ? LS_KEY_BASE : `${LS_KEY_BASE}.${c}`;
}
const POLL_MS = 12_000;
const MAX_SEEN = 1000;      // bound the dedupe set
const MAX_PRIME_PAGES = 25; // safety cap on the one-time fast-forward

type Persisted = { cursor: string; unread: Record<string, number>; seenIds: string[] };

interface Ctx {
  /** How many distinct members have a new inbound text since last seen. */
  unreadCount: number;
  /** Does any of these phones (last-10 or formatted) have a new inbound? */
  isUnread: (phones: Array<string | undefined>) => boolean;
  /** Clear the badge for these phones (called when a member's conversation is opened). */
  markSeen: (phones: Array<string | undefined>) => void;
  /** Clear every badge. No caller today (this comment once said the Inbound queue called it; it never did). */
  markAllSeen: () => void;
}

const SmsInboundContext = createContext<Ctx>({
  unreadCount: 0, isUnread: () => false, markSeen: () => undefined, markAllSeen: () => undefined,
});

export function useSmsInbound(): Ctx {
  return useContext(SmsInboundContext);
}

function last10(p?: string): string {
  return String(p ?? '').replace(/\D/g, '').slice(-10);
}

function loadState(): Persisted {
  try {
    const raw = localStorage.getItem(lsKey());
    if (raw) {
      const p = JSON.parse(raw) as Partial<Persisted>;
      return { cursor: p.cursor ?? '', unread: p.unread ?? {}, seenIds: p.seenIds ?? [] };
    }
  } catch { /* corrupt or unavailable storage: start clean */ }
  return { cursor: '', unread: {}, seenIds: [] };
}

/** A receive-only plan's stored state is another plan's data: remove it, start (and stay) empty. */
function purgeState(): Persisted {
  try { localStorage.removeItem(lsKey()); } catch { /* unavailable storage: nothing stored to remove */ }
  return { cursor: '', unread: {}, seenIds: [] };
}

export function SmsInboundProvider({ children }: { children: ReactNode }): JSX.Element {
  // Read once at mount: AppShell remounts this provider on a client switch (see lsKey).
  const standDown = useMemo(() => isReceiveOnly(currentClient()), []);
  const initial = useMemo(() => (standDown ? purgeState() : loadState()), [standDown]);
  const [unread, setUnread] = useState<Record<string, number>>(initial.unread);

  const cursorRef = useRef<string>(initial.cursor);
  const seenRef = useRef<Set<string>>(new Set(initial.seenIds));
  const unreadRef = useRef<Record<string, number>>(initial.unread);
  const primingRef = useRef<boolean>(false);
  const bustedRef = useRef<boolean>(false); // rail not configured: stand down

  const persist = useCallback(() => {
    if (standDown) return; // never store anything under a receive-only plan's key
    try {
      const seenArr = Array.from(seenRef.current).slice(-MAX_SEEN);
      seenRef.current = new Set(seenArr); // keep the in-memory set bounded too
      localStorage.setItem(lsKey(), JSON.stringify({ cursor: cursorRef.current, unread: unreadRef.current, seenIds: seenArr }));
    } catch { /* quota / unavailable: the badge is best-effort */ }
  }, [standDown]);

  const markSeen = useCallback((phones: Array<string | undefined>) => {
    const keys = phones.map(last10).filter(Boolean);
    if (!keys.length) return;
    setUnread((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const k of keys) if (k in next) { delete next[k]; changed = true; }
      if (!changed) return prev;
      unreadRef.current = next;
      persist();
      return next;
    });
  }, [persist]);

  const markAllSeen = useCallback(() => {
    setUnread((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      unreadRef.current = {};
      persist();
      return {};
    });
  }, [persist]);

  const poll = useCallback(async () => {
    if (standDown || bustedRef.current || primingRef.current) return;
    try {
      // PRIME: no cursor yet -> fast-forward past existing history, badging nothing.
      if (!cursorRef.current) {
        primingRef.current = true;
        try {
          for (let i = 0; i < MAX_PRIME_PAGES; i++) {
            const page = await getSmsInbound(cursorRef.current);
            if (!page.configured) { bustedRef.current = true; return; }
            for (const r of page.rows) seenRef.current.add(r.messageId);
            const nextCur = page.nextCursor ?? '';
            if (!nextCur || page.rows.length === 0) { cursorRef.current = nextCur || cursorRef.current; break; }
            cursorRef.current = nextCur;
          }
          // A primed cursor of '' would re-prime forever; stamp a sentinel so we advance to steady state.
          if (!cursorRef.current) cursorRef.current = 'primed';
          persist();
        } finally {
          primingRef.current = false;
        }
        return;
      }
      // STEADY STATE: badge genuinely new arrivals.
      const page = await getSmsInbound(cursorRef.current === 'primed' ? '' : cursorRef.current);
      if (!page.configured) { bustedRef.current = true; return; }
      const fresh: string[] = [];
      for (const r of page.rows) {
        if (seenRef.current.has(r.messageId)) continue;
        seenRef.current.add(r.messageId);
        const k = last10(r.fromPhone);
        if (k) fresh.push(k);
      }
      if (page.nextCursor) cursorRef.current = page.nextCursor;
      if (fresh.length) {
        setUnread((prev) => {
          const next = { ...prev };
          for (const k of fresh) next[k] = (next[k] ?? 0) + 1;
          unreadRef.current = next;
          persist();
          return next;
        });
      } else {
        persist(); // still advance the stored cursor + trim the seen set
      }
    } catch { /* transient feed outage: skip this tick, retry on the next */ }
  }, [persist, standDown]);

  useEffect(() => {
    if (standDown) return undefined; // no timer, no visibility listener: this plan has no feed
    void poll();
    const id = setInterval(() => { if (document.visibilityState === 'visible') void poll(); }, POLL_MS);
    const onVis = (): void => { if (document.visibilityState === 'visible') void poll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [poll, standDown]);

  const value = useMemo<Ctx>(() => ({
    unreadCount: Object.keys(unread).length,
    isUnread: (phones) => phones.some((p) => { const k = last10(p); return Boolean(k) && k in unread; }),
    markSeen,
    markAllSeen,
  }), [unread, markSeen, markAllSeen]);

  return <SmsInboundContext.Provider value={value}>{children}</SmsInboundContext.Provider>;
}
